// src/app/menu.jsx — le menu : familles d'onglets par rôle, famille active,
// bascules de l'orchestrateur, choix d'une famille. Déplacé à l'identique
// depuis src/App.jsx (P-3.5, squelette) ; calculé à chaque rendu, comme avant.

import {
  CalendarDays, CalendarRange,
  Home, SprayCan, MessageCircle, Building2, Wallet,
} from 'lucide-react'
import { parcEstDirty, GARDE_PARC_TEXTE } from '../views/logements/parcDirty'
import BasculeEspace from '../components/BasculeEspace'

export function construireMenu({
  tab, setTab, estControleur, isAdmin, isOrchestrateur,
}) {
  // Nav top-level réorganisée par catégorie métier (cf. ADR-018, Option 2 hybride).
  // Les composants enfants qui ont leurs propres sous-onglets internes (MessagesPanel,
  // ParamsPanel) ne déclarent qu'1 tab dans leur catégorie — la barre secondaire
  // est masquée à 1 tab pour ne pas dupliquer leur nav interne.
  // LE MENU DE L'ADMIN, MAQUETTE NEST v677 (P-4.24) : Au quotidien (Accueil ·
  // Calendrier · Opérations · Voyageurs), Mon parc (Logements · Argent). Les ids
  // d'onglets INTERNES ne bougent pas : tous les liens profonds tiennent.
  // `dansTiroir` : au téléphone, seul le menu ☰ les porte (la barre du bas garde
  // quatre raccourcis et l'opérateur au milieu, journal « Accueil »).
  // Les RÉGLAGES vivent derrière la pastille de compte (tab 'params', hors
  // CATEGORIES). La PROSPECTION vit dans Logements › À démarcher.
  const CATEGORIES = isAdmin ? [
    { id: "accueil", Icone: Home, label: "Accueil", tabs: [["accueil", "Accueil"]] },
    // L'écran `operateur` reste routable, hors CATEGORIES comme 'params' : on
    // l'atteint par l'Accueil et par le logo NEST de la barre du bas.
    { id: "calendrier", Icone: CalendarDays, label: "Calendrier", tabs: [["calendrier", "Calendrier"]] },
    // L'id `jour` reste celui de la famille (badges, sous-onglets, withJourAlias).
    { id: "jour", Icone: SprayCan, label: "Opérations", tabs: [
      ["menages",                "Les ménages"],
      ["menage-controle",        "Contrôle"],
      ["incidents",              "Incidents"],
      ["orchestrateur-horaires", "Demandes d'horaires"],
      ["stock",                  "Stock"],
      ["mes-taches",             "Tâches"],
    ]},
    { id: "voyageurs", Icone: MessageCircle, label: "Voyageurs", tabs: [
      ["messages",            "Messages"],
      ["voyageurs-textes",    "Textes automatiques"],
      ["voyageurs-avis",      "Avis"],
      ["voyageurs-demandes",  "Demandes directes"],
    ]},
    { id: "logements", Icone: Building2, dansTiroir: true, label: "Logements", tabs: [["logements", "Logements"]] },
    { id: "argent", Icone: Wallet, dansTiroir: true, label: "Argent", tabs: [
      ["donnees",     "Données"],                  // le seul à reconstruire (plus tard)
      ["facturation", "Facturation"],
      ["historique",  "Historique"],
      ["compta",      "Compta"],
      // Lot 5 (décision 3) : Statistiques RETIRÉ du menu — la bascule du
      // chrome Argent garde l'entrée, qui ouvre la phrase de révision.
    ]},
  ] : [
    // Phase 4 PR 4 + PR 5 fix (ADR-076) — menu AGENT simplifié (ménage +
    // orchestrateur). La branche else = isAgentUI (proprio a déjà `return` plus
    // haut ; seuls admin et agents arrivent ici).
    //
    // CHANTIER 42 Phase 3 (recalibrage) — l'ORCHESTRATEUR pilote via « Suivi
    // équipe » (écran UNIQUE : semaine + jour + alertes + actions). Il N'a PAS
    // l'onglet « Aujourd'hui » (l'écran agent) — « Suivi équipe » est son défaut,
    // en tête. L'agent ménage garde « Aujourd'hui » et ne voit JAMAIS « Suivi
    // équipe » (réservé isOrchestrateur).
    // NAV ORCHESTRATEUR 7 → 4 (maquette Dimitri 2026-08-19) : Planning
    // (Suivi + Mois — la même information à deux échelles de temps),
    // À décider (Demandes + Incidents + Contrôle — trois décisions), Stock,
    // Avis (relation client, seul). ⚠️ Réserve assumée notée au backlog : un
    // clic de plus vers Demandes — réversible si pénible à l'usage. Les IDS
    // D'ONGLETS INTERNES NE BOUGENT PAS : tous les liens profonds tiennent.
    // P-4.11 (30/10) — MAQUETTE v677 : six entrées (ESPACES.orch.ongs, journal
    // « Orchestrateur »), sans emoji. Les IDS D'ONGLETS NE BOUGENT PAS. Tranché
    // par la maquette, contre les « quatre onglets » de la nav du 19/08.
    ...(isOrchestrateur
      ? [{ id: "planning", label: "Les ménages", tabs: [
            ["menage-suivi", "Aujourd'hui"], ["menage-calendrier", "Le mois"]] },
         ...(estControleur ? [{ id: "menage-controle", label: "Contrôle", tabs: [["menage-controle", "Contrôle"]] }] : []),
         { id: "incidents", label: "Incidents", tabs: [["incidents", "Incidents"]] },
         { id: "a-decider", label: "Demandes d'horaires", tabs: [["orchestrateur-horaires", "Demandes"]] },
         { id: "stock", label: "Stock", tabs: [["stock", "Stock"]] },
         { id: "menage-avis", label: "Avis", tabs: [["menage-avis", "Avis"]] }]
      : [
         // REFONTE AGENT (cadrage validé 2026-08-22) — TROIS onglets, la barre
         // tenait pas à 430. Le Mois vit DANS Mon planning (bascule) ; les
         // contrôles DANS sa journée ; incidents/avis DANS Mon travail ; le
         // wizard de contrôle reste un onglet CACHÉ (menage-controle).
         // MAQUETTE NEST v677 (lot G2, 30/10) — DEUX onglets : Mon planning ·
         // Mon travail. Plus de « Mon sac » : l'orchestrateur dispatche le
         // stock ; l'agent n'a aucun stock et ne dépose rien, il signale un
         // manque et lit « À apporter » sur la carte d'un ménage (maquette).
         { id: "menage-today", label: "📅 Mon planning", tabs: [["menage-today", "📅 Mon planning"]] },
         { id: "mon-travail",  label: "⭐ Mon travail",  tabs: [["mon-travail",  "⭐ Mon travail"]] }]),
  ]

  // ⚠️ NULLE quand l'onglet vit HORS menu (réglages derrière la pastille) :
  // aucune destination ne s'allume alors — chez les agents le repli d'avant
  // est conservé.
  const activeCategory = CATEGORIES.find(c => c.tabs.some(([t]) => t === tab))
    || (isAdmin ? null : CATEGORIES[0])
  // Les entrées des bascules de page — partagées entre la copie du HAUT
  // (grand écran) et la copie SOUS LE BANDEAU (téléphone, spec 2026-08-22).
  const ENTREES_PLANNING = isOrchestrateur ? [
    { id: "menage-suivi", label: "Aujourd'hui", icone: CalendarDays },
    { id: "menage-calendrier", label: "Le mois", icone: CalendarRange },
  ] : []
  const basculePlanningMobile = isOrchestrateur ? (
    <div className="fbz-bascule-sous-bandeau">
      <BasculeEspace testId="bascule-planning-m" actif={tab} onChange={setTab} entrees={ENTREES_PLANNING} />
    </div>
  ) : null
  const setCategory = (catId) => {
    const cat = CATEGORIES.find(c => c.id === catId)
    if (!cat) return
    if (cat.tabs.some(([t]) => t === tab)) return  // déjà dans cette cat
    // LOT 4 (décision Dimitri 2026-08-28) — garde anti-perte : quitter la
    // destination Logements avec un brouillon non enregistré démonte la fiche
    // et perd la saisie. On demande d'abord.
    if (tab === 'logements' && parcEstDirty() && !window.confirm(GARDE_PARC_TEXTE)) return
    setTab(cat.tabs[0][0])  // bascule sur le premier sub
  }
  return {
    CATEGORIES, activeCategory, ENTREES_PLANNING, basculePlanningMobile, setCategory,
  }
}
